import * as React from 'react';
import {
  ClosableModal,
  ErrorMessage,
  FileUploader,
  PrimaryButton,
  PrimaryTextField,
  SecondaryButton,
} from 'components';
import { UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IBadge } from 'base/auth/interface';
import badgeModalValidation from './validation/badge-modal-validation';

interface Props {
  open: boolean;
  onClose: () => void;
  onContinue: (badge: IBadge) => void;
  badge: IBadge;
}

const BadgeUploadModal = ({
  open, onClose, onContinue, badge,
}: Props) => {
  const {
    control, handleSubmit, setValue, clearErrors, setError,
    formState: { errors },
  } = useForm<IBadge>({
    defaultValues: {
      badgeTitle: '',
      badgeDescription: '',
      badgeImgUrl: '',
    },
    resolver: yupResolver(badgeModalValidation),
    shouldFocusError: true,
  });
  const [badgeUrl, setBadgeUrl] = React.useState('');

  React.useEffect(() => {
    if (open) {
      clearErrors();
      setValue('badgeImgUrl', badge.badgeImgUrl);
      setValue('badgeTitle', badge.badgeTitle);
      setValue('badgeDescription', badge.badgeDescription);
      setBadgeUrl(badge.badgeImgUrl);
    }
  }, [open]);

  const handleUrl = (res: UploadResponse) => {
    clearErrors('badgeImgUrl');
    setValue('badgeImgUrl', res.url);
    if (res.statusCode !== 201) {
      setError('badgeImgUrl', { type: 'custom', message: res.message });
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleRemove = () => {
    clearErrors('badgeImgUrl');
    setValue('badgeImgUrl', '');
  };

  const onSubmit: SubmitHandler<IBadge> = (data) => {
    onContinue(data);
  };

  return (
    <ClosableModal
      open={open}
      onClose={onClose}
      title="Add new badge"
      subTitle=""
      modalClass="select-item-modal"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row mb-3 mt-n2">
          <div className="col-md-12">
            <FileUploader
              handleFile={handleUrl}
              handleRemove={handleRemove}
              currentContentUrl={badgeUrl}
              scenario={UploadScenarios.sessionBanner}
              currentMediaType="image"
              uploadBtnTxt="Upload badge"
            />
            <ErrorMessage>{errors?.badgeImgUrl?.message}</ErrorMessage>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="form-item">
              <Controller
                control={control}
                name="badgeTitle"
                render={({ field }) => <PrimaryTextField {...field} label="Badge title" />}
              />
              <ErrorMessage>{errors?.badgeTitle?.message}</ErrorMessage>
            </div>
            <div className="form-item">
              <Controller
                control={control}
                name="badgeDescription"
                render={({ field }) => <PrimaryTextField {...field} multiline rows={3} label="Badge description" />}
              />
              <ErrorMessage>{errors?.badgeDescription?.message}</ErrorMessage>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 col-md-6 mb-2 mb-md-0">
            <SecondaryButton fullWidth onClick={handleCancel}>
              Cancel
            </SecondaryButton>
          </div>
          <div className="col-12 col-md-6">
            <PrimaryButton fullWidth type="submit">
              Add & continue
            </PrimaryButton>
          </div>
        </div>
      </form>
    </ClosableModal>
  );
};

export default BadgeUploadModal;
